import { Link, NavLink, useLocation } from "react-router-dom";
import Styles from "./styles.module.scss";
import Logo from "../../assets/affinicar-logo.png";
import { useEffect, useState, ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComments, faChevronDown } from "@fortawesome/free-solid-svg-icons";

export type Link = {
  label: string;
  badge?: string;
  badgeClassName?: string;
  to?: string;
  target?: string;
}
export type LinkSection = {
  label: string;
  links?: Link[];
  /** Section repliée à l'ouverture (l'utilisateur peut la déplier au clic). */
  defaultCollapsed?: boolean;
}

function SectionLinks({ section }: { section: LinkSection }) {

  const [isExpanded, setIsExpanded] = useState(!section.defaultCollapsed);
  const linkSize = 40;

  // Somme des compteurs numériques des liens : affichée sur l'en-tête quand
  // la section est repliée, pour ne pas perdre le signal « il y a une action »
  // (ex : « 1 paiement en retard » caché par le repli).
  const collapsedBadgeTotal = (section.links ?? []).reduce((sum, l) => {
    const n = l.badge ? parseInt(l.badge, 10) : 0;
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);

  return (
    <div className={Styles.sectionContainer} key={section.label}>
      <div
        className={Styles.sectionLabel}
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <span className={Styles.sectionLabelText}>{section.label}</span>
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {!isExpanded && collapsedBadgeTotal > 0 && (
            <span className="badge bg-info">{collapsedBadgeTotal}</span>
          )}
          <FontAwesomeIcon
            icon={faChevronDown}
            style={{
              fontSize: "0.7rem",
              transition: "transform 0.2s ease-in-out",
              transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
            }}
          />
        </span>
      </div>
      {section.links?.length ? (
        <ul style={{width: '100%', height: isExpanded ? ((section.links.length) *linkSize) + 'px' : "0px"}}>
          {section.links.map((link) => (
            <li key={link.to}>
              <NavLink
                target={link.target}
                end className={({ isActive }) => {
                return [Styles.linkContainer, isActive ? Styles.linkContainerActive : ""].join(" ")
              }} to={link.to || "/"}>
                <FontAwesomeIcon className={Styles.linkIcon} icon={faComments} />
                <span className={Styles.linkLabel}>{link.label}</span>
                {!!link.badge && (
                  <span className={["badge bg-info", ...(link.badgeClassName ? [link.badgeClassName] : [])].join(" ")}>{link.badge}</span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      ) : (null)}
    </div>
  )

}

export default function Sidebar({
  linkSections = [],
  header,
  visible = false,
  size = "large",
  onPressCloseMenu = () => null
} : {
  linkSections: LinkSection[];
  header?: ReactNode;
  visible?: boolean;
  size?: "large" | "small";
  onPressCloseMenu?: () => any;
}) {

  const location = useLocation();

  useEffect(() => {
    onPressCloseMenu();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      <div onClick={onPressCloseMenu} className={[Styles.backgroundOpaqueLayer, visible ? Styles.visible : ""].join(" ")} />

      <div className={[Styles.containerPlaceholder, size === "small" ? Styles.small : ""].join(" ")}></div>

      <div className={[Styles.container, size === "small" ? Styles.small : "", visible ? Styles.visible : ""].join(" ")}>
        <div className={Styles.logoContainer}>
          <img src={Logo} />
        </div>

        {header && <div>{header}</div>}

        <nav>
          {linkSections.length ? (
            linkSections.map((section) => (
              <SectionLinks key={section.label} section={section} />
            ))
          ) : null}
        </nav>
      </div>

    </>
  )
}